<script setup lang="ts">
import { ref,reactive,onMounted,onBeforeUnmount,computed } from 'vue'
import CustomList from "../../compositions/CustomList.vue";
import TextBox from "../../compositions/TextBox.vue";
import SelectBox from "../../compositions/SelectBox.vue";
import ModalAppDialog from "../../compositions/ModalAppDialog.vue";
import PharmacyMedicineTagComponent from "../Common/PharmacyMedicineTagComponent.vue";
import config from "../../config.js";
import { errorAlertApp } from "../../plugins/alert_app";
import { getFieldValue,deepCopy } from "../../plugins/utility";
import { warningMark } from '../../plugins/medicine';
import { screenTransition,callBarcode,callSound } from "../../plugins/flutter";
import qs from 'qs';
import axios from '../../plugins/axios.ts';
import { useDeliveryStore } from '../../stores/delivery';

const delivery = useDeliveryStore();

const props = defineProps({
    apis:{
      type:Object,
      required:true,
    },
    user:{
      type:Object,
      required:true,
    },
    staffId:{
      type:Number,
      required:true,
    },
    recordId:{
      type:Number,
      required:true,
    },
    medicineGroupId:{
      type:String,
      required:true,
    },
    targetName:{
      type:String,
      required:true,
    },
    scanCode:{
      type:String,
      required:true,
    },
    stockType:{
      type:Number,
      required:true,
    },
    lotNo:{
      type:String,
      required:true,
    },
    expireDate:{
      type:String,
      required:true,
    },
    viewMode:{
      type:Number,
      required:true,
    },
})

const listDeliveryRef = ref();
const lotNo = ref(props.lotNo);
const viewMode = ref(props.viewMode);
const isDeliveryConfirmationError = ref(false);
const currentMedicine = reactive([]);
const expireDate = ref(props.expireDate);
const selectCurrentStockId = ref(0);
const selectCurrentMecidineId = ref(0);
const scanCode = ref(props.scanCode);
const deliveryFields = ref([
  {field_name: '', field_key: 'reset_action', width: '10%', align: 'text-center', sorted:false},
  {field_name: '採用品名', field_key: 'product_detail_name', width: '70%', align: 'text-left', sorted:false},
  {field_name: '', field_key: 'order_number', width: '20%', align: 'text-center', sorted:false},
]);
const deliveryOptionFields = reactive({
  'record_id' : props.recordId,
  'stock_type': props.stockType,
  'medicine_group_id': props.medicineGroupId,
  'page_size' : 30,
  'lot_no':lotNo.value,
  'scan_code':scanCode.value,
});

///////////////////////////////////////////////////////
const getTmpDeliveryMedicineId = (stockId,medicine) => {
  return (stockId > 0 ? stockId : 'tmp' + String(medicine.medicine_id) + '_' + lotNo.value);
}
const calcSameIncludeMedicineNumber = (medicine) => {
  let result = 0;
  //受信済みの場合は完全一致、そうでない場合は同じ薬品の未受信の者の総数で比較
  if (medicine.receive_response_code) {
    return medicine.order_number;
  } else {
    result = medicine.order_number;
    for (let i=0;i<delivery.delivery_medicines.length;i++) {
      if (medicine.receive_response_code || delivery.delivery_medicines[i].id == medicine.id) {
        continue;
      }
      if (delivery.delivery_medicines[i].medicine_id == medicine.medicine_id) {
        result += (delivery.delivery_medicines[i].order_number - delivery.delivery_medicines[i].number);
      }
    }
  }
  return result;
}
const checkMedicineOver = (medicine) => {
  let result = false;
  let currentStockId = getCurrentStockId(medicine.medicine_id,medicine.lot_no);
  let currentDeliveryMedicine = getDeliveryMedicine(currentStockId,medicine.medicine_id,medicine.lot_no);
  //VANでオーバーする場合は確認メッセージを出す
  //同じ薬品でまたVAN未受信のものがある場合はロット番号A、ロット番号B、ロット番号Aというように順番に通さない場合はアラートが出てしまうのでチェックする
  if (currentDeliveryMedicine != null &&
    props.stockType == config.STOCK_TYPES['VAN'] &&
    (currentDeliveryMedicine.number + 1) > calcSameIncludeMedicineNumber(currentDeliveryMedicine)
  ) {
    //ただし既に追加済みの場合は毎回確認メッセージを出すのは使いずらいので出さないようにする
    //手入力の時も出さないようにする
    if (((typeof currentDeliveryMedicine.is_add_confirmed === 'undefined') || currentDeliveryMedicine.is_add_confirmed == false) && viewMode.value == 0) {
      result = true;
    }
  }

  return result;
}
const addMedicine = (medicine,force_number = null) => {
  let currentStockId = getCurrentStockId(medicine.medicine_id,medicine.lot_no);
  if (currentStockId == 0 && typeof medicine.stock_id !== 'undefined' && medicine.stock_id) {
    //ページングでまだロードされていないケース対応
    currentStockId = medicine.stock_id;
  }
  let currentDeliveryMedicine = getDeliveryMedicine(currentStockId,medicine.medicine_id,medicine.lot_no,false);
  //既に登録済みで小分けの場合は上書きの必要がないのでスキップ
  if (getDeliveryMedicine(currentStockId,medicine.medicine_id,medicine.lot_no,true) != null && props.stockType == config.STOCK_TYPES['小分け']) {
    //追加しない
  } else {
    let number = 1;
    let orderNumber = (medicine.order_number ? medicine.order_number : 1);
    if (currentDeliveryMedicine != null) {
      if (props.stockType == config.STOCK_TYPES['VAN']) {
        number = currentDeliveryMedicine.number + 1;
      } else {
        number = currentDeliveryMedicine.order_number;
      }
      orderNumber = currentDeliveryMedicine.order_number;
      if (number > orderNumber) {
        orderNumber = number;
        currentDeliveryMedicine.is_add_confirmed = true;
      }
    } else {
      currentDeliveryMedicine = deepCopy(medicine);
      currentDeliveryMedicine.id = getTmpDeliveryMedicineId(currentStockId,medicine);
      currentDeliveryMedicine.stock_id = currentStockId;
      currentDeliveryMedicine.medicine_id = medicine.medicine_id;
      currentDeliveryMedicine.stock_type = props.stockType;
      currentDeliveryMedicine.lot_no = lotNo.value;
      currentDeliveryMedicine.expire_date = expireDate.value;
      currentDeliveryMedicine.is_add_confirmed = false;
      if (props.stockType == config.STOCK_TYPES['小分け']) {
        number = packNumber.value;
        currentDeliveryMedicine.unit_price = packPrice.value;
        orderNumber = number;
      } else {
        number = 1;
      }
    }
    currentDeliveryMedicine.number = number;
    if (props.stockType == config.STOCK_TYPES['VAN'] && currentDeliveryMedicine.receive_response_code == null && currentDeliveryMedicine.order_number < orderNumber) {
      currentDeliveryMedicine.order_number = orderNumber;
      //同じ薬品の数量を減らす
      for (let i=0;i<delivery.delivery_medicines.length;i++) {
        if (delivery.delivery_medicines[i].id == currentDeliveryMedicine.id || delivery.delivery_medicines[i].receive_response_code != null) {
          continue;
        }
        if (delivery.delivery_medicines[i].number < delivery.delivery_medicines[i].order_number) {
          delivery.delivery_medicines[i].order_number--;
          break;
        }
      }
    } else if (props.stockType == config.STOCK_TYPES['小分け'] && currentDeliveryMedicine.order_number == 0) {
      currentDeliveryMedicine.order_number = orderNumber;
    }
    if (currentDeliveryMedicine.lot_no == null || currentDeliveryMedicine.lot_no == '') {
      currentDeliveryMedicine.lot_no = lotNo.value;
    }
    if (currentDeliveryMedicine.expire_date == null || currentDeliveryMedicine.expire_date == '') {
      currentDeliveryMedicine.expire_date = expireDate.value;
    }
    if (force_number != null) {
      currentDeliveryMedicine.number = force_number;
    }
    delivery.save_data(currentDeliveryMedicine);
    //新規で追加したものはAPI内にはないので追加する
    if (currentDeliveryMedicine.stock_id == 0 && listDeliveryRef.value.existsCustomItem(currentDeliveryMedicine.id) == false) {
      listDeliveryRef.value.addCustomItems([currentDeliveryMedicine]);
    }
  }
  selectCurrentStockId.value = currentDeliveryMedicine.stock_id;
  //現在の採用品が変わる場合は一旦リロードする
  if (selectCurrentMecidineId.value > 0 && selectCurrentMecidineId.value != currentDeliveryMedicine.medicine_id) {
    listRefresh();
  }
  selectCurrentMecidineId.value = currentDeliveryMedicine.medicine_id;
}
const listRefresh = (delay=0) => {
  listDeliveryRef.value.resetItem(delay);
}
const getCurrentStockId = (medicineId,itemLotNo) => {
  //現在のまだ埋まっていない採用品を検索する(優先順位 ロット番号一致⇒ロット番号null)
  let result = 0;
  let deliveryIds = [];
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    if (delivery.delivery_medicines[i].medicine_id == medicineId) {
      deliveryIds.push(delivery.delivery_medicines[i].id);
    }
  }
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    if (delivery.delivery_medicines[i].medicine_id == medicineId && delivery.delivery_medicines[i].lot_no == itemLotNo) {
      if (delivery.delivery_medicines[i].order_number > delivery.delivery_medicines[i].number) {
        result = delivery.delivery_medicines[i].stock_id;
        break;
      }
    }
  }
  if (result == 0) {
    //まだ完全未登録なので、APIリストから取得する
    let items = listDeliveryRef.value.getItems();
    for (let i=0;i<items.length;i++) {
      if (deliveryIds.indexOf(items[i].id) != -1) {
        continue;
      }
      if (items[i].medicine_id == medicineId && items[i].lot_no && items[i].lot_no == itemLotNo) {
        result = items[i].stock_id;
        break;
      }
    }
    if (result == 0) {
      for (let i=0;i<items.length;i++) {
        if (deliveryIds.indexOf(items[i].id) != -1) {
          continue;
        }
        if (items[i].medicine_id == medicineId && (items[i].lot_no == null || items[i].lot_no == '')) {
          result = items[i].stock_id;
          break;
        }
      }
    }
  }
  return result;
}
const getSameDeliveryMedicine = (medicineId,itemLotNo) => {
  let result = null;
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    if (delivery.delivery_medicines[i].medicine_id == medicineId && delivery.delivery_medicines[i].lot_no != itemLotNo && delivery.delivery_medicines[i].number < delivery.delivery_medicines[i].order_number && delivery.delivery_medicines[i].stock_type == config.STOCK_TYPES['VAN'] && delivery.delivery_medicines[i].receive_response_code == null) {
      result = delivery.delivery_medicines[i];
      break;
    }
  }
  if (result == null) {
    //部分納品のものがあるかもしれないのでAPIリストから取得する
    let items = listDeliveryRef.value.getItems();
    for (let i=0;i<items.length;i++) {
      if (items[i].medicine_id == medicineId && items[i].lot_no != itemLotNo && items[i].stock_type == config.STOCK_TYPES['VAN'] && items[i].receive_response_code == null) {
        result = items[i];
        break;
      }
    }
  }
  return result;
}
const getDeliveryMedicine = (stockId,medicineId,itemlotNo,isApiExcept = false) => {
  let result = null;
  let deliveryIds = [];
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    if (delivery.delivery_medicines[i].medicine_id == medicineId) {
      deliveryIds.push(delivery.delivery_medicines[i].id);
    }
  }
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    if (stockId > 0 && delivery.delivery_medicines[i].stock_id == stockId) {
      result = delivery.delivery_medicines[i];
      break;
    } else if (stockId == 0 && delivery.delivery_medicines[i].medicine_id == medicineId && delivery.delivery_medicines[i].lot_no == itemlotNo) {
      result = delivery.delivery_medicines[i];
      break;
    }
  }
  if (result == null && isApiExcept == false) {
    //まだ完全未登録なので、APIリストから取得する
    let items = listDeliveryRef.value.getItems();
    for (let i=0;i<items.length;i++) {
      if (deliveryIds.indexOf(items[i].id) != -1) {
        continue;
      }
      if (stockId > 0 && items[i].stock_id == stockId) {
        result = items[i];
        break;
      } else if (stockId == 0 && items[i].medicine_id == medicineId && items[i].lot_no == itemlotNo) {
        result = items[i];
        break;
      }
    }
    // それでも見つからなかった場合はlot_noがまだないものなのでlot_no検索条件を外して再検索する
    if (result == null) {
      for (let i=0;i<items.length;i++) {
        if (deliveryIds.indexOf(items[i].id) != -1) {
          continue;
        }
        if (stockId == 0 && items[i].medicine_id == medicineId && (items[i].lot_no == null || items[i].lot_no == '')) {
          result = items[i];
          break;
        }
      }
    }
  }

  return result;
}

//////////////////////////////////////////////////////////
const errorDialogSubmitName = ref('登録する');
const isErrorDialog = ref(false);
const showErrorDialog = () => {
  packNumber.value = 0;
  isErrorDialog.value = true;
}
const closeErrorDialog = () => {
  isErrorDialog.value = false;
}
const isDeliveryConfirmationConfirm = ref(false);
const isDeliveryConfirmationConfirmLoading = ref(false);
const showDeliveryConfirmationConfirm = () => {
  packNumber.value = 0;
  isDeliveryConfirmationConfirm.value = true;
}
const closeDeliveryConfirmationConfirm = () => {
  isDeliveryConfirmationConfirm.value = false;
}

const updateDeliveryConfirmationCheck = async () => {
  isDeliveryConfirmationConfirmLoading.value = true;
  deleteTargetList.splice(0);
  let params = {
    staff_id:props.staffId,
    record_id:props.recordId,
    stocks:[],
    stock_type:props.stockType,
    medicine_group_id:props.medicineGroupId,
  }
  //不要なデータがあるとWAFにエラー判定されるので必要なフィールドのみ渡す
  for (let i=0;i<delivery.delivery_medicines.length;i++) {
    let record = {
      medicine_id:delivery.delivery_medicines[i].medicine_id,
      lot_no:delivery.delivery_medicines[i].lot_no,
      stock_id:delivery.delivery_medicines[i].stock_id,
      number:delivery.delivery_medicines[i].number,
      unit_price:delivery.delivery_medicines[i].unit_price,
      expire_date:delivery.delivery_medicines[i].expire_date,
      order_number:delivery.delivery_medicines[i].order_number,
      action_save_time:delivery.delivery_medicines[i].action_save_time,
    };
    if (typeof delivery.delivery_medicines[i].lot_number_order !== 'undefined') {
      record['lot_number_order'] = delivery.delivery_medicines[i].lot_number_order;
    }
    if (typeof delivery.delivery_medicines[i].number_order !== 'undefined') {
      record['number_order'] = delivery.delivery_medicines[i].number_order;
    }
    params.stocks.push(record);
  }
  try {
    const response = await axios(props.apis.pharmacy_delivery_confirmation_check_update,{method:"post",data:params});
    if (response.data.status == 200) {
      callSound(config.SPAPP_SOUND['完了']);
      screenTransition(config.SPAPP_MENU['HOME']);
    } else {
      for (let i=0;i<response.data.delet_target_list.length > 0;i++) {
        deleteTargetList.push(response.data.delet_target_list[i]);
      }
    }
  } finally {
    isDeliveryConfirmationConfirmLoading.value = false;
  }
}

const errorMedicine = async (medicine) => {
  errorMedicineData.splice(0);
  callSound(config.SPAPP_SOUND['エラー']);
  if (medicine.medicine_id == 0) {
    //採用品が見つからなかった場合
    errorDialogSubmitName.value = '';
    showErrorDialog();
  } else {
    errorDialogSubmitName.value = '登録する';
    let currentDeliveryMedicine = getDeliveryMedicine(0,medicine.medicine_id,medicine.lot_no,false);
    if (currentDeliveryMedicine == null) {
      currentDeliveryMedicine = deepCopy(medicine);
      currentDeliveryMedicine.stock_type = props.stockType;
      currentDeliveryMedicine.medicine_id = medicine.medicine_id;
      currentDeliveryMedicine.stock_id = 0;
      currentDeliveryMedicine.number = 0;
      currentDeliveryMedicine.order_number = 0;
      currentDeliveryMedicine.lot_no = lotNo.value;
      currentDeliveryMedicine.expire_date = expireDate.value;
    } else if (props.stockType == config.STOCK_TYPES['小分け']) {
      //小分けで既に追加済みの場合は何もしない
      selectCurrentStockId.value = currentDeliveryMedicine.stock_id;
      selectCurrentMecidineId.value = currentDeliveryMedicine.medicine_id;
      return;
    }
    errorMedicineData.push(currentDeliveryMedicine);
    showErrorDialog();
  }
}
const addErrorMedicine = () => {
  //小分けの場合は数量と単価が０の場合はエラーにする
  if (props.stockType == config.STOCK_TYPES['小分け']) {
    if (packNumber.value == 0) {
      errorAlertApp('数量を入力してください。');
      return;
    }
    if (packPrice.value == 0) {
      errorAlertApp('単価を入力してください。');
      return;
    }
  }
  let record = deepCopy(errorMedicineData[0]);
  if (record.medicine_type != config.NON_MEDICINE_TYPES['OTC・雑貨'] && isDeliveryConfirmationError.value && checkMedicineOver(record) == false) {
    //追加の場合は念のためロット番号と有効期限を必須にする
    if (lotNo.value.length == 0) {
      errorAlertApp('ロット番号を入力してください。');
      return;
    }
    if (expireDate.value.length == 0) {
      errorAlertApp('有効期限を入力してください。');
      return;
    }
    if (expireDate.value.length != 6) {
      errorAlertApp('有効期限は6桁で入力してください。');
      return;
    }
    record.lot_no = lotNo.value;
    record.expire_date = expireDate.value;
  }
  addMedicine(record);
  closeErrorDialog();
  closeSelfInputDialog();
}
const isMountedAddMedicine = ref(false);
const mountInitialData = (data) => {
  if (isMountedAddMedicine.value == false) {
    return;
  }
  if (viewMode.value == 0) {
    //雑貨以外でロット番号が無い場合はロット番号入力画面にする
    if (lotNo.value.length == 0 && scanCode.value.startsWith('49') == false) {
      isLotNoInput.value = true;
      viewMode.value = 2;
      isMountedAddMedicine.value = false;
      showSelfInputDialog();
      barcodeCode.value = scanCode.value;
      return;
    }
    //採用品を検索する
    let currentMedicineData = null;
    for (let i=0;i<data.list.length;i++) {
      if (scanCode.value.startsWith('04') && (data.list[i].dispensing_packaging_unit_code == scanCode.value || data.list[i].dispensing_packaging_unit_code2 == scanCode.value)) {
        currentMedicineData = data.list[i];
        break;
      } else if ((data.list[i].sales_packaging_unit_code == scanCode.value) || (data.list[i].sales_packaging_unit_code2 == scanCode.value)) {
        currentMedicineData = data.list[i];
        break;
      }
    }
    if (currentMedicineData == null) {
      let deliveryCheckData = getDeliveryMedicine(0,data.medicine.id,lotNo.value,true);
      if (deliveryCheckData == null) {
        isDeliveryConfirmationError.value = true;
        let newMedicine = deepCopy(data.medicine);
        newMedicine.lot_no = lotNo.value;
        newMedicine.expire_date = expireDate.value;
        newMedicine.is_add_confirmed = false;
        currentMedicine.push(newMedicine);
      } else {
        currentMedicine.push(deliveryCheckData);
      }
    } else {
      let deliveryMedicine = getDeliveryMedicine(0,currentMedicineData.medicine_id,lotNo.value);
      //VAN未受信で異なるロット番号の製品が来た場合の対応
      if (deliveryMedicine == null && props.stockType == config.STOCK_TYPES['VAN']) {
        let sameDeliveryMedicine = getSameDeliveryMedicine(currentMedicineData.medicine_id,lotNo.value);
        if (sameDeliveryMedicine != null) {
          //分割する
          let divideMedicine = deepCopy(sameDeliveryMedicine);
          divideMedicine.id = 0;
          divideMedicine.stock_id = 0;
          divideMedicine.lot_no = lotNo.value;
          divideMedicine.expire_date = expireDate.value;
          divideMedicine.order_number = sameDeliveryMedicine.order_number - sameDeliveryMedicine.number;
          divideMedicine.number = 0;
          divideMedicine.is_add_confirmed = false;
          deliveryMedicine = divideMedicine;
          let sameExists = false;
          //部分納品の場合は部分納品を０にして完了にする
          for (let i=0;i<delivery.delivery_medicines.length;i++) {
            if (delivery.delivery_medicines[i].id == sameDeliveryMedicine.id) {
              sameExists = true;
              delivery.delivery_medicines[i].order_number = delivery.delivery_medicines[i].number;
              break;
            }
          }
          if (sameExists == false) {
            //部分納品でロット番号違いがあり、今回は同じロット番号は０の場合の対応
            sameDeliveryMedicine.number = 0;
            sameDeliveryMedicine.order_number = 0;
            addMedicine(sameDeliveryMedicine,0);
          }
        }
      }
      if (deliveryMedicine == null) {
        isDeliveryConfirmationError.value = true;
        let newMedicine = deepCopy(currentMedicineData);
        newMedicine.lot_no = lotNo.value;
        newMedicine.expire_date = expireDate.value;
        newMedicine.is_add_confirmed = false;
        currentMedicine.push(newMedicine);
      } else {
        currentMedicine.push(deliveryMedicine);
      }
    }
    if (isDeliveryConfirmationError.value == false) {
      callSound(config.SPAPP_SOUND['通常']);
    } else {
      isMountedAddMedicine.value = false;
      errorMedicine(currentMedicine[0]);
    }
  } else if (viewMode.value == 2) {
    showSelfInputDialog();
  }
}
onMounted(() => {
  errorMedicineData.splice(0);
  currentMedicine.splice(0);
  isDeliveryConfirmationError.value = false;
  //システム対象外のバーコードの場合はエラーにする
  if (scanCode.value == 'unknown') {
    showSystemAlertDialog();
  } else {
    listDeliveryRef.value.clearCustomItems();
    let customItems = [];
    for (let i=0;i<delivery.delivery_medicines.length;i++) {
      if (delivery.delivery_medicines[i].stock_id == 0) {
        customItems.push(delivery.delivery_medicines[i]);
      }
    }
    if (customItems.length > 0) {
      listDeliveryRef.value.addCustomItems(customItems);
    }
    isMountedAddMedicine.value = true;
  }
});
const deliveryMedicineData = computed(() => {
  return (item) => {
    return getDeliveryMedicine(item.stock_id,item.medicine_id,item.lot_no);
  }
});
const isChecked = computed(() => {
  return (item) => {
    return (getDeliveryMedicine(item.stock_id,item.medicine_id,item.lot_no,true) != null ? true : false);
  }
});
const isCurrentDeliveryMedicine = computed(() => {
  return (item) => {
    let result = false;
    if (item.stock_id && selectCurrentStockId.value == item.stock_id) {
      result = true;
    } else if (item.stock_id == 0 && selectCurrentMecidineId.value == item.medicine_id && lotNo.value == item.lot_no) {
      result = true;
    }
    return result;
  }
});
const isCurrentDeliveryOver = computed(() => {
  return (medicine) => {
    return checkMedicineOver(medicine);
  }
});
const checkCompleteNumber = computed(() => {
  let result = 0;
    for (let i=0;i<delivery.delivery_medicines.length;i++) {
      if (delivery.delivery_medicines[i].order_number <= delivery.delivery_medicines[i].number) {
        result += 1;
      }
    }
  return result;
})
const medicineAllNumber = ref(0);
const responseComplete = (data) => {
  medicineAllNumber.value = data.count;
  mountInitialData(data);
  if (isMountedAddMedicine.value && viewMode.value != 1) {
      if (props.stockType == config.STOCK_TYPES['小分け'] && getDeliveryMedicine(getCurrentStockId(currentMedicine[0].medicine_id),currentMedicine[0].medicine_id,currentMedicine[0].lot_no) == null) {
        showPackDialog();
      } else {
        if (checkMedicineOver(currentMedicine[0])) {
          errorMedicine(currentMedicine[0]);
        } else {
          addMedicine(currentMedicine[0]);
        }
      }
  }
  isMountedAddMedicine.value = false;
}
const callDeviceBarcode = () => {
  callBarcode(config.SPAPP_MENU['納品確認']);
}
//////////////////////////////////
// 小分け入力対応
const packNumber = ref(0);
const packPrice = ref(0);
const onChangePackNumber = (value) => {
  packNumber.value = Number(value);
}
const onChangePackPrice = (value) => {
  packPrice.value = Number(value);
}
const onChangeLotNo = (value) => {
  lotNo.value = value;
}
const onChangeExpireDate = (value) => {
  expireDate.value = value;
}
const isPackDialog = ref(false);
const packMedicineData = reactive([]);
const showPackDialog = () => {
  packNumber.value = 0;
  packMedicineData.splice(0);
  packMedicineData.push(currentMedicine[0]);
  isPackDialog.value = true;
}
const closePackDialog = () => {
  callBarcode(config.SPAPP_MENU['納品確認']);
}
const addPackMedicine = () => {
  if (packNumber.value == 0) {
    errorAlertApp('数量を入力してください。');
    return;
  }
  if (packPrice.value == 0) {
    errorAlertApp('単価を入力してください。');
    return;
  }
  addMedicine(currentMedicine[0]);
  isPackDialog.value = false;
}
//////////////////////////////////
//////////////////////////////////
// 手入力対応
const selectMedicine = reactive([]);
const selectOrgMedicineOptions = computed(() => {
  let result = [];
  return result;
});
const isSelfInputDialog = ref(false);
const isLotNoInput = ref(false);
const isCheckLoading = ref(false);
const barcodeCode = ref('');
const medicineType = ref(0);
const errorMedicineData = reactive([]);
const changeSelfText = (value,option) => {
  if (option.type == 'code') {
    barcodeCode.value = value;
  }
}
const showSelfInputDialog = () => {
  isMountedAddMedicine.value = false;
  barcodeCode.value = '';
  packNumber.value = 0;
  medicineType.value = 0;
  isSelfInputDialog.value = true;
}
const closeSelfInputDialog = () => {
  isSelfInputDialog.value = false;
  isLotNoInput.value = false;
}
const onSelectChangeMedicine = (item) => {
  selectMedicine.splice(0);
  selectMedicine.push(item);
  barcodeCode.value = item.sales_packaging_unit_code;
  medicineType.value = item.medicine_type;
}
const checkSelfInputMedicineCheck = async () => {
  if (barcodeCode.value == null || barcodeCode.value.length == 0) {
    errorAlertApp('コードを入力してください。');
    return;
  }
  if (medicineType.value != config.NON_MEDICINE_TYPES['OTC・雑貨']) {
    if (lotNo.value.length == 0) {
      errorAlertApp('ロット番号を入力してください。');
      return;
    }
    if (expireDate.value.length == 0) {
      errorAlertApp('有効期限を入力してください。');
      return;
    }
    if (expireDate.value.length != 6) {
      errorAlertApp('有効期限は6桁で入力してください。');
      return;
    }
  }
  isCheckLoading.value = true;
  let params = new FormData();
  params.append('record_id',props.recordId);
  params.append('code',barcodeCode.value);
  params.append('medicine_group_id',props.medicineGroupId);
  params.append('stock_type',props.stockType);
  params.append('lot_no',lotNo.value);
  params.append('expire_date',expireDate.value);

  try {
    const response = await axios(props.apis.pharmacy_delivery_confirmation_code_check,{method:"post",data:params});
    if (response.data.status == 200) {
      medicineType.value = response.data.data.medicine_type;
      if (response.data.data.medicine_id == 0) {
        scanCode.value = barcodeCode.value;
        isDeliveryConfirmationError.value = true;
        errorMedicine(response.data.data);
      } else {
        isMountedAddMedicine.value = true;
        viewMode.value = 0;
        //再ロード
        deliveryOptionFields.lot_no = lotNo.value;
        deliveryOptionFields.scan_code = barcodeCode.value;
        scanCode.value = barcodeCode.value;
        listRefresh(config.LIST_DELAY);

        closeSelfInputDialog();
      }
    }
  } finally {
    isCheckLoading.value = false;
  }
}
const selectMedicineId = computed(() => {
  return selectMedicine.length > 0 ? selectMedicine[0].id : 0;
})
// 手入力対応
//////////////////////////////////
//システム対象外バーコード
const isSystemAlertDialog = ref(false);
const showSystemAlertDialog = () => {
  isSystemAlertDialog.value = true;
}
const closeSystemAlertDialog = () => {
  isSystemAlertDialog.value = false;
}

/// wait_number対応
const deleteTargetList = reactive([]);
const isReset = computed(() => {
  return (deliveryMedicine) => {
    if (deliveryMedicine == null) {
      return false;
    } else {
      let result = false;
      for (let i=0;i<deleteTargetList.length;i++) {
        if (deleteTargetList[i].stock_id == deliveryMedicine.stock_id && deleteTargetList[i].medicine_id == deliveryMedicine.medicine_id) {
          result = true;
          break;
        }
      }
      return result;
    }
  }
})
const deleteDeliveryMedicine = (deliveryMedicine) => {
  if (deliveryMedicine != null) {
    delivery.delete_data(deliveryMedicine.stock_id,deliveryMedicine.medicine_id);
  }
}
</script>

<style scoped>
table td {
  overflow-wrap: break-word;
  word-wrap: break-word;
  white-space: normal;
}
.list {
  border: 1px solid #ccc;
}
table {
  width:100%;
  border-collapse:collapse;
}
table > tbody > tr {
  border-bottom:1px solid #ccc;
}
table > tbody > tr > td {
  padding:0.5em;
  border-right:1px solid #ccc;
}
.selected {
background-color:#ccc;
}
.current-selected {
  background-color:#F59E12;
  color:white;
}
.button {
  background-color:#F59E12;
  color:white;
  width:100%;
  border-radius: 0.5em;
  padding:0.5em 3em 0.5em 3em;
}
.submit-button {
  background-color: #1235F5;
  color: white;
  color:white;
  width:100%;
  border-radius: 0.5em;
  padding:0.5em 3em 0.5em 3em;
}
</style>
<template>
<div ref="element" class="relative">
  <div class="w-full p-2">
    <p class="text-lg mb-1">【{{ targetName }}】</p>
    <p class="text-lg mb-2">納品確認チェック状況　({{ checkCompleteNumber }} / {{ medicineAllNumber }})</p>
    <custom-list
      ref="listDeliveryRef"
      :table-fields="deliveryFields"
      :option-fields="deliveryOptionFields"
      :api-url="apis.pharmacy_current_order_delivery_list"
      :is-table-layout-fixed="true"
      :response-complete="responseComplete"
      font-size="1.0em"
      height="75vh"
    >
    <template v-slot:item="slotProps">
      <td v-for="(field,index) in deliveryFields" :key="index" :style="'width:' + field.width"  :class="(isCurrentDeliveryMedicine(slotProps.item) == true ? 'current-selected ' : (isChecked(slotProps.item) == true ? 'selected' : ''))">
        <div v-if="field.field_key == 'product_detail_name'" :class="field.align">
          <span v-html="slotProps.item.product_detail_name + warningMark(slotProps.item) + (deliveryMedicineData(slotProps.item) != null ? (deliveryMedicineData(slotProps.item).lot_no != null ? (deliveryMedicineData(slotProps.item).lot_no ? '<br>ロット番号:' : '') + deliveryMedicineData(slotProps.item).lot_no : '') : (slotProps.item.lot_no ? '<br>ロット番号:' : '') + slotProps.item.lot_no)"></span>
          <div v-if="typeof slotProps.item.tags !== 'undefined' && slotProps.item.tags.length > 0" class="mt-2">
            <pharmacy-medicine-tag-component
              :tags="slotProps.item.tags"
              :apis="apis"
              :pharmacy-medicine-id="0"
              :is-edit="false"
            ></pharmacy-medicine-tag-component>
          </div>
        </div>
        <div v-else-if="field.field_key == 'reset_action'" :class="field.align">
          <a v-show="isReset(deliveryMedicineData(slotProps.item))" href="javascript:void(0)" @click="deleteDeliveryMedicine(deliveryMedicineData(slotProps.item))"><i class="fa-regular fa-square-minus"></i></a>
        </div>
        <div v-else :class="field.align">
          <div v-if="slotProps.item.stock_type == config.STOCK_TYPES['VAN']">
            {{ (deliveryMedicineData(slotProps.item) != null ? deliveryMedicineData(slotProps.item).number : slotProps.item.number) }}/{{ (deliveryMedicineData(slotProps.item) != null ? deliveryMedicineData(slotProps.item).order_number : slotProps.item.order_number) }}
          </div>
          <div v-else>
            {{ (deliveryMedicineData(slotProps.item) != null ? deliveryMedicineData(slotProps.item).order_number : slotProps.item.order_number) }}{{ slotProps.item.package_unit }}
          </div>
        </div>
      </td>
    </template>
    </custom-list>
    <div class="mt-2 mb-4">
      <button type="button" @click="callDeviceBarcode()" class="button">バーコード読み込み</button>
    </div>
    <div class="mb-1">
      <button type="button" @click="showDeliveryConfirmationConfirm()" class="submit-button">確認完了</button>
    </div>
  </div>
</div>
<modal-app-dialog
  :is-visible="isPackDialog"
  :close="closePackDialog"
  :submit="addPackMedicine"
  cancel-button-name="キャンセル"
  submit-button-name="登録する"
>
  <template v-slot:default>
  <div class="mb-6">
    <p class="text-lg mb-1">【{{ targetName }}】</p>
    <div v-for="packMedicineRecord in packMedicineData" :key="packMedicineRecord.id">
      <div class="text-lg text-gray-700">
        納品数を入力してください。<br><br>
      </div>
      <div class="text-lg text-gray-700 mb-2">
        {{ packMedicineRecord.product_name + ' ' + packMedicineRecord.package_label }}
      </div>
      <table v-show="stockType == config.STOCK_TYPES['小分け']" class="w-full">
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">数量({{ packMedicineRecord.package_unit }})</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="packNumber"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :is-focus-clear="false"
              type="number"
              :on-change="onChangePackNumber"
            ></text-box>
          </td>
        </tr>
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">単価</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="packPrice"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :is-focus-clear="false"
              type="number"
              :on-change="onChangePackPrice"
            ></text-box>
          </td>
        </tr>
      </table>
    </div>
  </div>
  </template>
</modal-app-dialog>

<modal-app-dialog
  :is-visible="isErrorDialog"
  :close="closeErrorDialog"
  :submit="addErrorMedicine"
  cancel-button-name="キャンセル"
  :submit-button-name="errorDialogSubmitName"
>
  <template v-slot:default>
  <div class="mb-6">
    <p class="text-lg mb-1">【{{ targetName }}】</p>
    <div v-for="errorMedicineRecord in errorMedicineData" :key="errorMedicineRecord.id">
      <div v-if="isDeliveryConfirmationError && isCurrentDeliveryOver(errorMedicineRecord) == false" class="text-lg text-gray-700">
        予定納品にはありません。<br>納品するには登録するを押してください。<br><br>
      </div>
      <div v-else class="text-lg text-gray-700">
        予定納品数を超えています。登録しますか？<br><br>
      </div>
      <div class="text-lg text-gray-700 mb-2">
        {{ errorMedicineRecord.product_detail_name }}
        <div v-if="typeof errorMedicineRecord.tags !== 'undefined' && errorMedicineRecord.tags.length > 0">
          <pharmacy-medicine-tag-component
            :tags="errorMedicineRecord.tags"
            :apis="apis"
            :pharmacy-medicine-id="0"
            :is-edit="false"
          ></pharmacy-medicine-tag-component>
        </div>
        <div v-show="(!isDeliveryConfirmationError || isCurrentDeliveryOver(errorMedicineRecord) == true) && errorMedicineRecord.lot_no">ロット番号:{{ errorMedicineRecord.lot_no }}</div>
        <div v-show="(!isDeliveryConfirmationError || isCurrentDeliveryOver(errorMedicineRecord) == true) && errorMedicineRecord.expire_date">有効期限:{{ errorMedicineRecord.expire_date }}</div>
      </div>
      <table v-show="stockType == config.STOCK_TYPES['小分け']" class="w-full">
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">数量({{ errorMedicineRecord.package_unit }})</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="packNumber"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :is-focus-clear="false"
              type="number"
              :on-change="onChangePackNumber"
            ></text-box>
          </td>
        </tr>
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">単価</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="packPrice"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :is-focus-clear="false"
              type="number"
              :on-change="onChangePackPrice"
            ></text-box>
          </td>
        </tr>
      </table>
      <table v-show="errorMedicineRecord.medicine_type != config.NON_MEDICINE_TYPES['OTC・雑貨'] && isDeliveryConfirmationError && isCurrentDeliveryOver(errorMedicineRecord) == false" class="w-full">
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">ロット番号</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="lotNo"
              type="text"
              font-width="half"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :on-change="onChangeLotNo"
            ></text-box>
          </td>
        </tr>
        <tr class="border-b border-white">
          <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">有効期限</th>
          <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
            <text-box
              :value="expireDate"
              type="text"
              font-width="half"
              font-size="1.125rem"
              font-line-height="1.75rem"
              :on-change="onChangeExpireDate"
              place-holder="250500"
            ></text-box>
          </td>
        </tr>
      </table>
    </div>
    <div v-show="errorMedicineData.length == 0" class="text-lg text-red-700">
      採用品マスタに存在しません。管理者にお問い合わせください。
      <span v-show="scanCode.length > 0">コード：{{ scanCode }}</span>
    </div>
  </div>
  </template>
</modal-app-dialog>

<modal-app-dialog
  :is-visible="isDeliveryConfirmationConfirm"
  :is-loading="isDeliveryConfirmationConfirmLoading"
  :close="closeDeliveryConfirmationConfirm"
  :submit="updateDeliveryConfirmationCheck"
  cancel-button-name="キャンセル"
  submit-button-name="登録する"
>
  <template v-slot:default>
  <div class="mb-6">
    <div class="text-lg">納品確定しますか？</div>
  </div>
  </template>
</modal-app-dialog>

<modal-app-dialog
  :is-visible="isSelfInputDialog"
  :close="closeSelfInputDialog"
  :submit="checkSelfInputMedicineCheck"
  cancel-button-name="キャンセル"
  submit-button-name="登録する"
  :is-loading="isCheckLoading"
>
  <template v-slot:default>
  <div class="mb-6">
    <p class="text-lg mb-1">【{{ targetName }}】</p>
    <div v-show="isLotNoInput == false" class="m-1">
      <select-box
        :options="selectOrgMedicineOptions"
        :on-change-object="onSelectChangeMedicine"
        font-size="1.125rem"
        font-line-height="1.75rem"
        list-field-name="product_detail_name"
        list-field-name-option="relation_exists_message"
        list-id-name="id"
        list-height="20em"
        :value="selectMedicineId"
        :api-url="apis.medicine_list"
        place-holder="採用品名を入力"
        :api-option-fields="{relation_pharmacy:1,is_except_relation:null}"
      ></select-box>
    </div>
    <table class="w-full">
      <tr v-show="isLotNoInput == false" class="border-b border-white">
        <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">コード</th>
        <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
          <text-box
            :value="barcodeCode"
            type="text"
            font-width="half"
            font-size="1.125rem"
            font-line-height="1.75rem"
            :value-options="{type:'code'}"
            :on-change="changeSelfText"
          ></text-box>
        </td>
      </tr>
      <tr v-show="medicineType != config.NON_MEDICINE_TYPES['OTC・雑貨']" class="border-b border-white">
        <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">ロット番号</th>
        <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
          <text-box
            :value="lotNo"
            type="text"
            font-width="half"
            font-size="1.125rem"
            font-line-height="1.75rem"
            :on-change="onChangeLotNo"
          ></text-box>
        </td>
      </tr>
      <tr v-show="medicineType != config.NON_MEDICINE_TYPES['OTC・雑貨']" class="border-b border-white">
        <th class="block px-3 py-2 bg-gray-200 text-left md:table-cell md:w-1/3 md:text-sm">有効期限</th>
        <td class="block py-3 text-sm md:table-cell md:w-2/3 md:px-3 md:py-2">
          <text-box
            :value="expireDate"
            type="text"
            font-width="half"
            font-size="1.125rem"
            font-line-height="1.75rem"
            :on-change="onChangeExpireDate"
            place-holder="250500"
          ></text-box>
        </td>
      </tr>
    </table>
  </div>
  </template>
</modal-app-dialog>

<modal-app-dialog
  :is-visible="isSystemAlertDialog"
  :close="closeSystemAlertDialog"
  cancel-button-name="キャンセル"
>
  <template v-slot:default>
  <div class="mb-6">
    <div class="text-red-500">このバーコードはシステム対象外です。他のバーコードを読み込んでください。</div>
  </div>
  </template>
</modal-app-dialog>

</template>

